/* eslint-disable no-nested-ternary */
import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import InputField from 'components/InputField';
import Button from 'components/Button';
import { getLocaleMap } from 'utils/locales';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'components/Select';
import Image from 'components/Image';
import { getFileData } from 'utils/file';
import { getErrorMessage, handleErrorResponse } from 'utils/general';
import { createBigFile } from 'api/files';
import { get } from 'lodash';
import { useAuth } from 'contexts/auth';
import { createModel, updateModel } from 'api/monitors';
import { withLoading } from 'utils/hoc';

const EditModelForm = ({ model,
  editButtonColor,
  editFontColor,
  saveButtonColor,
  saveButtonFontColor,
  backButtonColor,
  backButtonFontColor,
  setLoading,
  ...props
}) => {
  const [lm, setLm] = useState({});
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [imageFile, setImageFile] = useState();
  const [imageFileData, setImageFileData] = useState();
  const formikRef = useRef();

  const [searchParams] = useSearchParams();
  const brandId = searchParams.get('brand_id');

  useEffect(() => {
    getLocaleMap([
      'errors_required',
      'save_button',
    ])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const getFormValidation = (values) => {
    const { id_name, id_code } = values;
    const errors = {};
    if (!id_code) {
      errors.id_code = lm.errors_required;
    }
    if (!id_name) {
      errors.id_name = lm.errors_required;
    }
    return errors;
  };

  const initialValues = useMemo(() => ({
    id_code: model?.id_code || null,
    id_name: model?.id_name || null,
    // year: model?.year || null,
    // month: model?.month || null,
    alias: model?.alias || null,
    status: model?.status || null,
    collection: model?.collection || null,
    model_number: model?.model_number || null,
    dial_code: model?.dial_code || null,
    diameter: model?.diameter || null,
    bracelet: model?.bracelet || null,
    case_material: model?.case_material || null,
    dial_description: model?.dial_description || null,
    hk_price: model?.hk_price || null,
    us_price: model?.us_price || null,
    cn_price: model?.cn_price || null,
    remarks: model?.remarks || null,
    default_image: model?.default_image || null,
  }), [model]);

  const handleChangeFile = useCallback(async (event, setFieldValue) => {
    const binaryData = [];
    if (event.target.files.length > 0) {
      binaryData.push(event.target.files[0]);
      const file = window.URL.createObjectURL(new Blob(binaryData, { type: 'image/*' }))
      setImageFile(file);
      setFieldValue('default_image', file.file_url);
      const res = await getFileData(event.target.files[0]);
      setImageFileData(res);
    }
  }, []);

  const handleDeleteImageFile = useCallback((setFieldValue) => {
    setFieldValue('default_image', null);
    setImageFile(null);
  }, []);

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onUploadFile = useCallback(() => {
    if (imageFileData) {
      const fileBody = { ...imageFileData };
      const { file_url } = imageFileData;
      const type = get(file_url, 'type');
      fileBody.$term_mime_type = type;
      return createBigFile(fileBody)
        .then(({ data }) => {
          setImageFileData(null);
          return data.file_id;
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth);
        });
    }
  }, [imageFileData, setAuth]);

  const updateModelToApi = useCallback((formik, fileId) => {
    const { values, setFieldError } = formik;
    if (!model) {
      return Promise.resolve();
    }
    const defaultImage = fileId ? { default_image: fileId } : (!values.default_image ? { default_image: null } : null);
    const updateBody = {
      id_code: values.id_code || null,
      id_name: values.id_name || null,
      // year: values.year,
      // month: values.month,
      alias: values.alias,
      ...(defaultImage && { ...defaultImage }),
      status: values.status,
      collection: values.collection,
      model_number: values.model_number,
      dial_code: values.dial_code,
      diameter: values.diameter,
      bracelet: values.bracelet,
      case_material: values.case_material,
      dial_description: values.dial_description,
      hk_price: values.hk_price ? (Number.isNaN(Number(values.hk_price)) ? values.hk_price : Number(values.hk_price).toLocaleString()) : null,
      us_price: values.us_price ? (Number.isNaN(Number(values.us_price)) ? values.us_price : Number(values.us_price).toLocaleString()) : null,
      cn_price: values.cn_price ? (Number.isNaN(Number(values.cn_price)) ? values.cn_price : Number(values.cn_price).toLocaleString()) : null,
      remarks: values.remarks,
    };
    setLoading(true);
    return updateModel(model.model_id, updateBody)
      .then(() => {
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', getErrorMessage(err));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [model, navigate, setLoading]);

  const createModelToApi = useCallback((formik, fileId) => {
    const { values, setFieldError } = formik;
    const createBody = {
      id_code: values.id_code || null,
      id_name: values.id_name || null,
      // year: values.year,
      // month: values.month,
      alias: values.alias,
      ...(fileId && { default_image: fileId }),
      brand: brandId,
      status: values.status,
      collection: values.collection,
      model_number: values.model_number,
      dial_code: values.dial_code,
      diameter: values.diameter,
      bracelet: values.bracelet,
      case_material: values.case_material,
      dial_description: values.dial_description,
      hk_price: values.hk_price ? (Number.isNaN(Number(values.hk_price)) ? values.hk_price : Number(values.hk_price).toLocaleString()) : null,
      us_price: values.us_price ? (Number.isNaN(Number(values.us_price)) ? values.us_price : Number(values.us_price).toLocaleString()) : null,
      cn_price: values.cn_price ? (Number.isNaN(Number(values.cn_price)) ? values.cn_price : Number(values.cn_price).toLocaleString()) : null,
    };
    setLoading(true);
    return createModel(createBody)
      .then(() => {
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', getErrorMessage(err));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId, navigate, setLoading]);

  const onSubmit = useCallback(() => {
    if (formikRef.current) {
      setLoading(true);
      const uploadFilePromise = onUploadFile();
      Promise.resolve(uploadFilePromise)
        .then((fileId) => {
          if (model) {
            updateModelToApi(formikRef.current, fileId);
          } else {
            createModelToApi(formikRef.current, fileId);
          }
        })
        .catch((err) => {
          formikRef.current.setFieldError('form', err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [createModelToApi, model, onUploadFile, updateModelToApi, setLoading]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validate={getFormValidation}
      enableReinitialize
      {...props}
    >
      {({ handleChange, handleBlur, setFieldValue, errors, values, touched, isSubmitting, dirty }) => {
        return (
          <MKBox component="form" role="form">
            <Grid container spacing={1}>
              <Grid item xs={12} mt={2}>
                <MKBox display="flex" width="100%" justifyContent="space-between">
                  <Button
                    color={backButtonColor || 'info'}
                    disabled={isSubmitting}
                    style={{
                      border: backButtonColor === 'white' && '1px solid black',
                      width: '10vw',
                    }}
                    onClick={() => { navigate(-1); }}
                    sx={{
                      color: backButtonFontColor,
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={onSubmit}
                    color={saveButtonColor}
                    disabled={isSubmitting || !dirty}
                    style={{
                      width: '10vw',
                    }}
                    sx={{
                      color: saveButtonFontColor,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                    ) : lm.save_button}
                  </Button>
                </MKBox>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Product Code
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="id_code"
                    label=""
                    value={values.id_code}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.id_code && !!errors.id_code}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.id_code && !!errors.id_code) ? errors.id_name : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Product Name
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="id_name"
                    label=""
                    value={values.id_name}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.id_name && !!errors.id_name}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.id_name && !!errors.id_name) ? errors.id_name : '\xa0'}
              </MKTypography>
              {/* <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Year
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="year"
                    label=""
                    value={values.year}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.year && !!errors.year}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.year && !!errors.year) ? errors.year : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Month
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <Select
                    name="month"
                    value={values.month}
                    onChange={(v) => setFieldValue('month', v)}
                    options={[
                      { label: 'Jan', value: 'jan' },
                      { label: 'Feb', value: 'feb' },
                      { label: 'Mar', value: 'mar' },
                      { label: 'Apr', value: 'apr' },
                      { label: 'May', value: 'may' },
                      { label: 'Jun', value: 'jun' },
                      { label: 'Jul', value: 'jul' },
                      { label: 'Aug', value: 'aug' },
                      { label: 'Sep', value: 'sep' },
                      { label: 'Oct', value: 'oct' },
                      { label: 'Nov', value: 'nov' },
                      { label: 'Dec', value: 'dec' },
                    ]}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.month && !!errors.month) ? errors.month : '\xa0'}
              </MKTypography> */}
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Alias
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="alias"
                    label=""
                    value={values.alias}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.alias && !!errors.alias}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.alias && !!errors.alias) ? errors.alias : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="flex-start">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Default Image
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <MKBox display="flex" flexDirection="column">
                    <MKBox mb="1rem" width="140px" height={imageFile || values.default_image?.file_url ? 'auto' : '140px'} sx={{ backgroundColor: !(imageFile || values.default_image?.file_url) && '#c8c8c8' }}>
                      <Image src={imageFile || values.default_image?.file_url} width="200px" />
                    </MKBox>
                    <MKBox display="flex">
                      <MKBox
                        onClick={handleImageUploadClick}
                        display="flex"
                        padding="5px"
                        justifyContent="center"
                        mr="1rem"
                        width="140px"
                        style={{ cursor: 'pointer', backgroundColor: '#114c86', border: '1px solid black', color: 'white' }}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => handleChangeFile(e, setFieldValue)}
                        />
                        Upload Image
                      </MKBox>
                      <MKBox
                        display="flex"
                        padding="5px"
                        justifyContent="center"
                        width="140px"
                        onClick={() => handleDeleteImageFile(setFieldValue)}
                        style={{ cursor: 'pointer', backgroundColor: '#c20000', border: '1px solid black', color: 'white' }}
                      >
                        Delete
                      </MKBox>
                    </MKBox>
                  </MKBox>
                </Grid>
              </Grid>

              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Status
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <RadioGroup
                    row
                    aria-labelledby="status-radio-buttons-group-label"
                    name="status"
                    value={values.status}
                    error={touched.status && !!errors.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <FormControlLabel value="In Production" control={<Radio />} label="In Production" />
                    <FormControlLabel value="Discontinued" control={<Radio />} label="Discontinued" />
                  </RadioGroup>
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Collection
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="collection"
                    label=""
                    value={values.collection}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.collection && !!errors.collection}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Model Number
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="model_number"
                    label=""
                    value={values.model_number}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.model_number && !!errors.model_number}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Dial Code
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="dial_code"
                    label=""
                    value={values.dial_code}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.dial_code && !!errors.dial_code}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Diameter
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="diameter"
                    label=""
                    value={values.diameter}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.diameter && !!errors.diameter}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Bracelet
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="bracelet"
                    label=""
                    value={values.bracelet}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.bracelet && !!errors.bracelet}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Case Material
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="case_material"
                    label=""
                    value={values.case_material}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.case_material && !!errors.case_material}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Dial Description
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="dial_description"
                    label=""
                    value={values.dial_description}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.dial_description && !!errors.dial_description}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    HK Price
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="hk_price"
                    label=""
                    value={values.hk_price}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.hk_price && !!errors.hk_price}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    US Price
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="us_price"
                    label=""
                    value={values.us_price}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.us_price && !!errors.us_price}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    CN Price
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="cn_price"
                    label=""
                    value={values.cn_price}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.cn_price && !!errors.cn_price}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center" mt="1rem">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Remarks
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="remarks"
                    label=""
                    value={values.remarks}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.remarks && !!errors.remarks}
                  />
                </Grid>
              </Grid>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditModelForm.propTypes = {
  model: PropTypes.object,
  onSave: PropTypes.func,
  editButtonColor: PropTypes.string,
  editFontColor: PropTypes.string,
  saveButtonColor: PropTypes.string,
  saveButtonFontColor: PropTypes.string,
  backButtonColor: PropTypes.string,
  backButtonFontColor: PropTypes.string,
  setLoading: PropTypes.func,
};

EditModelForm.defaultProps = {
  model: {},
  onSave: () => { },
  setLoading: () => {},
};

export default withLoading(EditModelForm);
